import React from "react";
import { useNotificationPreferences } from "@/hooks/useNotificationPreferences";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle, Shield } from "lucide-react";

interface NotificationPreference {
  id: string;
  label: string;
  description: string;
  enabled: boolean;
  warning?: string;
}

// Mapping entre les IDs frontend et les colonnes de la base de données
const PREFERENCE_MAPPING = {
  'referent_validation': 'referent_validation_enabled',
  'candidate_refusal': 'candidate_refusal_enabled',
  'campaign_completed': 'campaign_completed_enabled',
  'report_generated': 'report_generated_enabled',
  'fraud_alert': 'fraud_alert_enabled'
} as const;

export function NotificationSettings() {
  const { preferences: dbPrefs, loading, saving, savePreferences } = useNotificationPreferences();
  
  const [globalEnabled, setGlobalEnabled] = React.useState(true);
  const [preferences, setPreferences] = React.useState<NotificationPreference[]>([
    {
      id: "referent_validation",
      label: "Validation des référents",
      description: "Confirmation que le candidat a renseigné les coordonnées de ses référents",
      enabled: true,
    },
    {
      id: "candidate_refusal",
      label: "Notification de refus",
      description: "Email automatique si le candidat refuse ou ne répond pas dans le délai",
      enabled: true,
    },
    {
      id: "campaign_completed",
      label: "Campagne terminée",
      description: "Notification quand tous les référents ont répondu ou que le délai est expiré",
      enabled: true,
    },
    {
      id: "report_generated",
      label: "Rapport généré",
      description: "Email avec le rapport IA (lien + PDF téléchargeable)",
      enabled: true,
    },
    {
      id: "fraud_alert",
      label: "Alerte fraude",
      description: "Email d'alerte si suspicion de fraude (ex. IP identique entre candidat et référent)",
      enabled: true,
      warning: "Ces alertes concernent la sécurité de votre compte. Nous vous recommandons de les laisser activées."
    },
  ]);

  // Charger les préférences depuis la base de données
  React.useEffect(() => {
    if (dbPrefs) {
      setGlobalEnabled(dbPrefs.global_enabled ?? true);
      setPreferences(prev => 
        prev.map(pref => ({
          ...pref,
          enabled: dbPrefs[PREFERENCE_MAPPING[pref.id as keyof typeof PREFERENCE_MAPPING]] ?? true
        }))
      );
    }
  }, [dbPrefs]);

  const togglePreference = (id: string) => {
    setPreferences(prev => 
      prev.map(pref => 
        pref.id === id ? { ...pref, enabled: !pref.enabled } : pref
      )
    );
  };

  const toggleGlobal = () => {
    const newGlobalState = !globalEnabled;
    setGlobalEnabled(newGlobalState);
    setPreferences(prev => 
      prev.map(pref => ({ ...pref, enabled: newGlobalState }))
    );
  };

  const handleSave = async () => {
    // Construire l'objet de mise à jour
    const updates = {
      global_enabled: globalEnabled,
      referent_validation_enabled: preferences.find(p => p.id === 'referent_validation')?.enabled ?? true,
      candidate_refusal_enabled: preferences.find(p => p.id === 'candidate_refusal')?.enabled ?? true,
      campaign_completed_enabled: preferences.find(p => p.id === 'campaign_completed')?.enabled ?? true,
      report_generated_enabled: preferences.find(p => p.id === 'report_generated')?.enabled ?? true,
      fraud_alert_enabled: preferences.find(p => p.id === 'fraud_alert')?.enabled ?? true,
    };

    await savePreferences(updates);
  };

  // Vérifier si toutes les notifications sont désactivées
  const allDisabled = !globalEnabled || preferences.every(p => !p.enabled);

  // Afficher un skeleton pendant le chargement
  if (loading) {
    return (
      <div className="w-full inline-flex flex-col justify-start items-start gap-5">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-10 w-64" />
      </div>
    );
  }



  return (
    <div className="w-full inline-flex flex-col justify-start items-start gap-5">
      {/* Avertissement si toutes les notifications sont désactivées */}
      {allDisabled && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Attention</AlertTitle>
          <AlertDescription>
            Vous ne recevrez plus aucune notification par email. Vous risquez de manquer des informations importantes sur vos campagnes.
          </AlertDescription>
        </Alert>
      )}

      {/* Header with global toggle */}
      <div className="self-stretch inline-flex justify-between items-start gap-5">
        <div className="flex-1 inline-flex flex-col justify-start items-start gap-1">
          <div className="self-stretch justify-start text-gray-800 text-2xl font-medium font-['Bricolage_Grotesque']">
            Notifications par e-mail
          </div>
          <div className="justify-center text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque']">
            Gérez vos préférences de notification pour rester informé des événements importants
          </div>
        </div>
        <div 
          className={`w-12 h-7 p-1 rounded-[55.36px] flex items-center transition-all duration-200 flex-shrink-0 mr-5 ${
            saving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
          } ${
            globalEnabled 
              ? 'bg-teal-400 justify-end' 
              : 'bg-neutral-400/20 justify-start'
          }`}
          onClick={saving ? undefined : toggleGlobal}
        >
          <div className="w-5 h-5 bg-white rounded-full shadow-sm"></div>
        </div>
      </div>

      {/* Notification preferences */}
      {preferences.map((preference) => (
        <div key={preference.id} className="self-stretch flex flex-col gap-2">
          <div
            className="p-5 rounded-xl outline outline-1 outline-offset-[-1px] outline-gray-200 inline-flex justify-between items-center"
          >
            <div className="inline-flex flex-col justify-center items-start gap-1 flex-1">
              <div className="justify-start text-black text-xl font-medium font-['Bricolage_Grotesque']">
                {preference.label}
              </div>
              <div className="justify-start text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque']">
                {preference.description}
              </div>
            </div>
            <div 
              className={`w-12 h-7 p-1 rounded-[55.36px] flex items-center transition-all duration-200 ${
                saving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
              } ${
                preference.enabled 
                  ? 'bg-teal-400 justify-end' 
                  : 'bg-neutral-400/20 justify-start'
              }`}
              onClick={saving ? undefined : () => togglePreference(preference.id)}
            >
              <div className="w-5 h-5 bg-white rounded-full shadow-sm"></div>
            </div>
          </div>
          
          {/* Avertissement spécifique pour certaines notifications */}
          {preference.warning && preference.enabled === false && (
            <Alert className="bg-amber-50 border-amber-200">
              <Shield className="h-4 w-4 text-amber-600" />
              <AlertTitle className="text-amber-900">Recommandation</AlertTitle>
              <AlertDescription className="text-amber-800">
                {preference.warning}
              </AlertDescription>
            </Alert>
          )}
        </div>
      ))}

      {/* Save button */}
      <button
        className={`h-10 px-6 py-3 bg-teal-400 rounded-[10px] inline-flex justify-center items-center gap-2 overflow-hidden transition-all ${
          saving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-teal-500'
        }`}
        onClick={handleSave}
        disabled={saving}
      >
        {saving && (
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
        )}
        <div className="justify-center text-white text-sm font-semibold font-['Bricolage_Grotesque']">
          {saving ? "Sauvegarde en cours..." : "Enregistrer les préférences"}
        </div>
      </button>
    </div>
  );
}